// Guardrail config constants, shared by the DiVA config UI (Views/AIChatbots.js). Mirrors
// languages.js. The stored shape (chatbot.guardrails) is read by diva-ai's engine.js
// (guardrailsFor / screenInput) and enforced by twilio.js — keep the keys in sync with
// diva-ai/engine.js DEFAULT_GUARDRAILS.
//
// Prompt-injection screening (a free keyword filter) is ALWAYS ON and cannot be disabled —
// it has no enable toggle. Off-topic and profanity are opt-in. All rules share ONE error
// path (the top-level action/message/fallback fields), not one per rule.

// The input guardrail rules, in display order. `key` matches the diva-ai rule key.
// `alwaysOn` rules have no enable toggle in the UI.
export const GUARDRAIL_RULES = [
	{
		key: 'promptInjection',
		label: 'Prompt injection / jailbreak',
		alwaysOn: true,
		desc: 'Always on. A keyword filter blocks attempts to override the assistant’s instructions, extract its hidden prompt, or make it act as a different, unrestricted AI. Optionally add an AI classifier to catch paraphrased attempts.'
	},
	{
		key: 'offTopic',
		label: 'Off-topic / out of scope',
		desc: 'Blocks questions outside the topics you allow. Requires an AI classifier call and a scope description of what the assistant IS allowed to help with.'
	},
	{
		key: 'profanity',
		label: 'Profanity / abuse',
		desc: 'Blocks abusive or profane language. Keyword filter only — no model call, no added latency.'
	}
];

// What happens when a caller trips ANY guardrail (one shared error path). `retry` is unique
// to guardrails (the failure-handling actions don’t offer it); transfer/hangup/flow mirror
// failurePolicy.
export const GUARDRAIL_ACTIONS = [
	{ id: 'retry', label: 'Retry', desc: 'Speak a message and keep listening (does not end the call). Repeated trips still escalate via the failure policy.' },
	{ id: 'transfer', label: 'Transfer', desc: 'Hand the caller to a fallback queue (business-hours gated).' },
	{ id: 'hangup', label: 'Hang up', desc: 'Speak a message and end the call.' },
	{ id: 'flow', label: 'Send to flow', desc: 'Redirect the caller to an IVR flow.' }
];

// Defaults. The action/fallback/closed fields (the shared error path) match diva-ai’s
// DEFAULT_FAILURE_POLICY so a trip can reuse the same escalation machinery. Prompt injection
// has no `enabled` (always on); its only knob is the opt-in classifier.
export const DEFAULT_GUARDRAILS = {
	action: 'retry',
	message: '',
	fallbackQueueId: '',
	fallbackFlowId: '',
	closedAction: 'disconnect',
	closedFlowId: '',
	rules: {
		promptInjection: { useClassifier: false },
		offTopic: { enabled: false, scope: '' },
		profanity: { enabled: false }
	}
};

// Deep-ish clone + backfill of a stored guardrails object against the defaults, so the
// editor always has every field even for a partial/absent config.
export function normalizeGuardrails(g) {
	var src = g && typeof g === 'object' ? g : {};
	var rules = src.rules && typeof src.rules === 'object' ? src.rules : {};
	return {
		action: src.action || DEFAULT_GUARDRAILS.action,
		message: src.message || '',
		fallbackQueueId: src.fallbackQueueId || '',
		fallbackFlowId: src.fallbackFlowId || '',
		closedAction: src.closedAction || 'disconnect',
		closedFlowId: src.closedFlowId || '',
		rules: {
			promptInjection: Object.assign({ useClassifier: false }, rules.promptInjection || {}),
			offTopic: Object.assign({ enabled: false, scope: '' }, rules.offTopic || {}),
			profanity: Object.assign({ enabled: false }, rules.profanity || {})
		}
	};
}
